import { useEffect, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { LoginForm } from "../components/ui/login-form";
import { AuthLayout } from "../components/layouts/AuthLayout";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { supabase } from "@/lib/supabase";
import { useTurnstile } from "@/lib/turnstile";
import { safeRedirectPath } from "@/lib/safe-redirect";

export function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signIn, loading, error, session } = useAuth();
  const [magicLinkOpen, setMagicLinkOpen] = useState(false);
  const [magicLinkEmail, setMagicLinkEmail] = useState("");
  const [magicLinkSubmitting, setMagicLinkSubmitting] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  // Single Turnstile widget shared by both the password form and the
  // magic-link form. Supabase enforces captcha globally once the
  // dashboard toggle is on (Auth → Attack Protection → Captcha), so
  // every signInWithPassword / signInWithOtp call must pass a token.
  const turnstile = useTurnstile();
  const captchaRequired = !turnstile.skipped;
  const captchaReady = !captchaRequired || turnstile.captchaToken !== null;

  // `?redirect=` only honoured for same-origin paths; see safeRedirectPath.
  const safeRedirect = safeRedirectPath(searchParams.get("redirect"));

  // Redirect if already logged in
  useEffect(() => {
    if (!loading && session) {
      navigate(safeRedirect, { replace: true });
    }
  }, [loading, session, navigate, safeRedirect]);

  const handleSubmit = async (data: { email: string; password: string }) => {
    if (captchaRequired && turnstile.captchaToken === null) {
      toast.error("Please complete the captcha.");
      return;
    }
    try {
      const session = await signIn({
        ...data,
        // Supabase accepts `undefined` (not `null`) as "no captcha";
        // hook returns `null` when Turnstile is skipped locally.
        captchaToken: turnstile.captchaToken ?? undefined,
      });
      if (session) {
        toast.success("Successfully logged in!");
        navigate(safeRedirect, { replace: true });
      } else if (error) {
        toast.error(error.message);
      }
    } finally {
      // Single-use token — discard regardless of outcome so a retry
      // gets a fresh challenge.
      turnstile.reset();
    }
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!magicLinkEmail) return;
    if (captchaRequired && turnstile.captchaToken === null) {
      toast.error("Please complete the captcha.");
      return;
    }
    setMagicLinkSubmitting(true);
    try {
      // A deep link (e.g. the OAuth consent page, ADR-058) has to survive
      // the trip through the inbox: the link lands back on this page with
      // the same `redirect`, and the signed-in effect above forwards it.
      // Plain logins keep Supabase's default redirect (ADR-050).
      const emailRedirectTo =
        safeRedirect === "/"
          ? undefined
          : `${window.location.origin}/login?redirect=${encodeURIComponent(safeRedirect)}`;
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: magicLinkEmail,
        options: {
          captchaToken: turnstile.captchaToken ?? undefined,
          emailRedirectTo,
        },
      });
      if (otpError) throw otpError;
      setMagicLinkSent(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send link");
    } finally {
      turnstile.reset();
      setMagicLinkSubmitting(false);
    }
  };

  return (
    <AuthLayout>
      <div className="flex flex-col items-center gap-4 w-full">
        <LoginForm
          onSubmit={handleSubmit}
          loading={loading}
          error={error?.message}
          submitDisabled={!captchaReady}
        />
        <div className="w-full max-w-md flex flex-col gap-2 px-1">
          {!magicLinkOpen && !magicLinkSent && (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => {
                setMagicLinkOpen(true);
              }}
            >
              Email me a magic link
            </Button>
          )}
          {magicLinkOpen && !magicLinkSent && (
            <form
              onSubmit={handleMagicLink}
              className="flex flex-col gap-2 border rounded-md p-3"
            >
              <Label htmlFor="magic-link-email">Email</Label>
              <Input
                id="magic-link-email"
                type="email"
                value={magicLinkEmail}
                onChange={(e) => {
                  setMagicLinkEmail(e.target.value);
                }}
                required
                autoComplete="email"
                disabled={magicLinkSubmitting}
              />
              <Button
                type="submit"
                className="w-full"
                disabled={magicLinkSubmitting || !captchaReady}
              >
                {magicLinkSubmitting ? "Sending..." : "Send magic link"}
              </Button>
            </form>
          )}
          {magicLinkSent && (
            <div className="text-sm text-center text-muted-foreground">
              Magic link sent to <strong>{magicLinkEmail}</strong>. Check your
              inbox.
            </div>
          )}
          <p className="text-xs text-muted-foreground text-center mt-2">
            New here?{" "}
            <Link
              to="/signup"
              className="underline underline-offset-2 decoration-accent-mint hover:text-foreground transition-colors"
            >
              Create an account
            </Link>
          </p>
          {captchaRequired && (
            <>
              <div
                ref={turnstile.ref}
                data-testid="turnstile-widget"
                className="flex justify-center"
              />
              {turnstile.failed && (
                <Alert variant="destructive">
                  <AlertDescription>
                    Couldn&apos;t load the security check. Please refresh the
                    page or check your network connection.
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}
        </div>
      </div>
    </AuthLayout>
  );
}
